// PlanoAssinaturaForm.tsx
import React, { useState, useEffect } from 'react';
import type { Plano } from '../planoTypes';
import AssinaturaStepConfirmacao from './AssinaturaStepConfirmacao';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { formatPlanPrice, getPlanBillingLabel, getPlanPriceSuffix } from '../planosUtils';
import { useAppContext } from '../../../../contexts/AppContext';
import {
    validateClienteStep,
    ClienteErrors,
} from './assinaturaCliente.validation';
import { api } from '../../../../AxiosConfig';

export interface AssinaturaFormData {
    // Contato
    customerEmail: string;
    customerPhone: string;

    // Assinatura
    acceptTerms: boolean;
}

const initialForm: AssinaturaFormData = {
    customerEmail: '',
    customerPhone: '',
    acceptTerms: false,
};

interface PlanoAssinaturaFormProps {
    plano: Plano | null;
    estabId: string;
    estabName?: string;
    back: any;
    onSuccess: () => void;
}

const PlanoAssinaturaForm: React.FC<PlanoAssinaturaFormProps> = ({
                                                                     plano,
                                                                     estabId,
                                                                     estabName,
                                                                     back,
                                                                     onSuccess,
                                                                 }) => {
    const { user } = useAuthContext();
    const { successSnackbar } = useAppContext();

    const [form, setForm] = useState<AssinaturaFormData>(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 🔹 erros por step
    const [clienteErrors, setClienteErrors] = useState<ClienteErrors>({});

    // 🔹 erros vindos do backend
    const [backendErrors, setBackendErrors] = useState<string[]>([]);

    // 🔹 Pré-popula dados a partir do usuário logado
    useEffect(() => {
        if (!user) return;

        setForm((prev) => ({
            ...prev,
            customerEmail: prev.customerEmail || user.email || '',
            customerPhone: prev.customerPhone || user.phoneNumber || '',
        }));
    }, [user]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const target = e.target;
        const { name, value, type } = target;

        setForm((prev) => ({
            ...prev,
            [name]:
                type === 'checkbox'
                    ? (target as HTMLInputElement).checked
                    : value,
        }));

        // Campos de contato: limpa erro daquele campo
        if (['customerEmail', 'customerPhone'].includes(name)) {
            const fieldName = name as keyof ClienteErrors;

            setClienteErrors((prev) => {
                if (!prev || !prev[fieldName]) return prev;
                const clone = { ...prev };
                delete clone[fieldName];
                return clone;
            });
        }

    };

    const validateContato = (): boolean => {
        setError(null);

        const errors = validateClienteStep(form);
        setClienteErrors(errors);

        if (Object.keys(errors).length > 0) {
            setError(
                'Revise o e-mail e telefone antes de continuar.',
            );
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setBackendErrors([]);

        if (!plano) {
            setError('Selecione um plano para continuar.');
            return;
        }

        if (!estabId) {
            setError(
                'Nenhum estabelecimento selecionado. Volte e escolha um estabelecimento para contratar o plano.',
            );
            return;
        }

        if (!validateContato()) {
            return;
        }

        if (!form.acceptTerms) {
            setError(
                'Você precisa aceitar os termos de uso e contratação para continuar.',
            );
            return;
        }

        setSubmitting(true);

        try {
            // 🔹 monta payload para o CreateAssinaturaClienteDto
            const payload = {
                planoId: plano._id,
                customerEmail: form.customerEmail,
                customerPhone: form.customerPhone,
            };

            await api.post(
                '/assinaturas/cliente/create',
                payload,
                {
                    headers: estabId
                        ? { 'x-estab-id': estabId }
                        : undefined,
                },
            );

            successSnackbar('Assinatura criada com sucesso!');
            onSuccess();
        } catch (err: any) {
            console.error(err);

            const respData = err?.response?.data;
            let messages: string[] = [];

            // 1) Se o backend já devolver `errors: string[]`
            if (Array.isArray(respData?.errors)) {
                messages = respData.errors;
            }

            // 3) Se ainda não tiver mensagens, usa a message padrão da resposta
            if (!messages.length && typeof respData?.message === 'string') {
                messages = [respData.message];
            }

            if (!messages.length) {
                messages = ['Erro ao confirmar a assinatura.'];
            }

            setBackendErrors(messages);
            // usa a primeira como mensagem principal
            setError(messages[0]);
        } finally {
            setSubmitting(false);
        }
    };

    if (!plano) {
        return (
            <div className="sf-state">
                <h2>Selecione um plano para continuar</h2>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="estab-settings space-y-6">
            <article className="pla-card">
                <div className="pla-card-body">
                    <h2 className="pla-card-name">{plano.name}</h2>
                    {plano.description && <p className="pla-card-description">{plano.description}</p>}
                    <div>
                        <p className="pla-price">
                            {formatPlanPrice(plano.price)}
                            <span>{getPlanPriceSuffix(plano.periodicity)}</span>
                        </p>
                        <p className="pla-billing">{getPlanBillingLabel(plano)}</p>
                    </div>
                    {!!estabName && <p className="pla-billing">{estabName}</p>}
                </div>
            </article>

            <AssinaturaStepConfirmacao
                form={form}
                onChange={handleChange}
                plano={plano}
                errors={clienteErrors}
            />

            {error && (
                <div className="pla-error" role="alert">
                    <span>{error}</span>
                    {backendErrors.slice(1).map((msg, idx) => (
                        <span key={idx}>{msg}</span>
                    ))}
                </div>
            )}

            <div className="estab-savebar gap-2">
                <button
                    type="button"
                    onClick={back}
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                    disabled={submitting}
                >
                    Voltar
                </button>

                <button
                    type="submit"
                    className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                    disabled={submitting || !form.acceptTerms}
                >
                    {submitting && <span className="loading loading-spinner loading-sm"></span>}
                    Confirmar assinatura
                </button>
            </div>
        </form>
    );
};

export default PlanoAssinaturaForm;
